import type { FormikConfig } from "formik";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

import * as FullStory from "@citrine/integrations/full-story";
import type { DocumentMetadata } from "@citrine/resources/catalyst";
import {
  CatalystClient,
  isModifiedConfigResponse,
} from "@citrine/resources/catalyst";
import { useConfiguration } from "@citrine/resources/configuration";
import { DocumentClient } from "@citrine/resources/document";

import type {
  CatalystMode,
  CatalystPredictorState,
} from "#src/contexts/catalyst";

import type {
  CatalystForm,
  CatalystQuestionAndAnswer,
  LiteratureQuestionAndAnswer,
  ModelQuestionAndAnswer,
} from "../types";
import {
  isLiteratureQuestionAndAnswer,
  isModelQuestionAndAnswer,
} from "../types";

export function useSubmitQuestion({
  mode,
  predictor,
  setQuestionsAndAnswers,
}: {
  mode: CatalystMode;
  predictor: CatalystPredictorState | undefined;
  setQuestionsAndAnswers: Dispatch<SetStateAction<CatalystQuestionAndAnswer[]>>;
}) {
  const { formatMessage } = useIntl();
  const fullStoryEvent = FullStory.useEvent();

  const { mutateAsync: askLiteratureQuestion } =
    CatalystClient.getInstance().useAskLiteratureQuestion();
  const { mutateAsync: askModelQuestion } =
    CatalystClient.getInstance().useAskModelQuestion();

  const enablePrivateDocuments = useConfiguration("enablePrivateDocuments");
  const { mutateAsync: askPrivateLiteratureQuestion } =
    DocumentClient.getInstance().useSearch();

  const predictorRef = useRef<CatalystPredictorState | undefined>(undefined);
  predictorRef.current = predictor;

  const modeRef = useRef<CatalystMode>(null);
  modeRef.current = mode;

  return useCallback<FormikConfig<CatalystForm>["onSubmit"]>(
    async ({ question }, { setFieldValue }) => {
      const newQuestionAndAnswerId = v4();
      fullStoryEvent(`catalyst.${mode}.question-asked`);

      // Clear the input immediately on submit (matching the agent flow) rather
      // than waiting for the answer to come back. Not `resetForm`, which would
      // also flip `isSubmitting` back to false mid-request.
      setFieldValue("question", "", false);

      try {
        if (mode === "model-expert") {
          // This shouldn't happen unless we have a bug
          /* v8 ignore if */
          if (!predictor) {
            throw Error(
              "Attempted to ask Model Expert question with no Catalyst predictor state",
            );
          }
          const predictorId = predictor.id;
          const predictorVersion = predictor.version;

          // Fill in the `answer` when it comes back, add entry without `answer`
          // to ensure that the order is correct (i.e. if the Catalyst mode is
          // changed while waiting for an answer)
          setQuestionsAndAnswers((prevQuestionsAndAnswers) => [
            ...prevQuestionsAndAnswers,
            {
              type: "question",
              catalystMode: "model-expert",
              id: newQuestionAndAnswerId,
              question,
            },
          ]);
          const answer = await askModelQuestion({
            question,
            config: predictor.config,
          });

          const isModifiedConfig = isModifiedConfigResponse(answer);

          // Do not show the user the option to Cancel/Apply changes if Catalyst
          // is no longer in Model Expert mode or a different predictor is now
          // selected (or no predictor is present).
          const automaticallyDiscardedMessage = !isModifiedConfig
            ? undefined
            : modeRef.current !== "model-expert"
              ? formatMessage({
                  id: "catalyst.modified-config.auto-cancel.catalyst-mode-changed",
                })
              : !predictorRef.current
                ? formatMessage({
                    id: "catalyst.modified-config.auto-cancel.predictor-builder-exited",
                  })
                : predictorRef.current?.id !== predictorId ||
                    predictorRef.current?.version !== predictorVersion
                  ? formatMessage({
                      id: "catalyst.modified-config.auto-cancel.predictor-changed",
                    })
                  : undefined;

          setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
            prevQuestionsAndAnswers.map((prevQuestionAndAnswer) =>
              isModelQuestionAndAnswer(prevQuestionAndAnswer) &&
              prevQuestionAndAnswer.id === newQuestionAndAnswerId
                ? ({
                    ...prevQuestionAndAnswer,
                    answer,
                    error: undefined,
                    ...(isModifiedConfig
                      ? {
                          automaticallyDiscardedMessage,
                          state: automaticallyDiscardedMessage
                            ? "rejected"
                            : "idle",
                        }
                      : {}),
                  } satisfies ModelQuestionAndAnswer)
                : prevQuestionAndAnswer,
            ),
          );
        } else {
          // Fill in the `answer` when it comes back, add entry without `answer`
          // to ensure that the order is correct (i.e. if the Catalyst mode is
          // changed while waiting for an answer)
          setQuestionsAndAnswers((prevQuestionsAndAnswers) => [
            ...prevQuestionsAndAnswers,
            {
              type: "question",
              catalystMode: "literature-expert",
              id: newQuestionAndAnswerId,
              question,
            },
          ]);

          const [publicAnswer, privateAnswer] = await Promise.allSettled([
            askLiteratureQuestion(question),
            enablePrivateDocuments
              ? askPrivateLiteratureQuestion(question)
              : Promise.reject({}),
          ]);

          // both questions errored out
          if (
            publicAnswer.status === "rejected" &&
            privateAnswer.status === "rejected"
          ) {
            throw publicAnswer.reason;
          }

          const answer =
            privateAnswer.status === "fulfilled"
              ? {
                  response: privateAnswer.value.answer,
                  document_metadata:
                    privateAnswer.value.documents.map<DocumentMetadata>(
                      (document) => ({
                        source_type: "private",
                        source: document.title,
                        id: document.id,
                      }),
                    ),
                  relevant_documents: privateAnswer.value.documents.length > 0,
                }
              : publicAnswer.status === "fulfilled"
                ? publicAnswer.value
                : undefined;

          setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
            prevQuestionsAndAnswers.map((prevQuestionAndAnswer) =>
              isLiteratureQuestionAndAnswer(prevQuestionAndAnswer) &&
              prevQuestionAndAnswer.id === newQuestionAndAnswerId
                ? ({
                    ...prevQuestionAndAnswer,
                    answer,
                    error: undefined,
                  } satisfies LiteratureQuestionAndAnswer)
                : prevQuestionAndAnswer,
            ),
          );
        }

        fullStoryEvent(`catalyst.${mode}.question-answered`);
        // Note: placing the `catch` here may be swallowing client-side errors
      } catch {
        fullStoryEvent(`catalyst.${mode}.question-errored`);

        // If we get an error response back from the Catalyst or Literature Expert,
        // replace the CatalystQuestionAndAnswer associated with the answerId with
        // a QuestionError.
        setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
          prevQuestionsAndAnswers.map((prevQuestionAndAnswer) =>
            (isLiteratureQuestionAndAnswer(prevQuestionAndAnswer) ||
              isModelQuestionAndAnswer(prevQuestionAndAnswer)) &&
            prevQuestionAndAnswer.id === newQuestionAndAnswerId
              ? {
                  type: "question",
                  catalystMode: isLiteratureQuestionAndAnswer(
                    prevQuestionAndAnswer,
                  )
                    ? "literature-expert"
                    : "model-expert",
                  error: formatMessage({
                    id: "catalyst.error-with-question",
                  }),
                  id: newQuestionAndAnswerId,
                  question,
                }
              : prevQuestionAndAnswer,
          ),
        );
      }
    },
    [
      askLiteratureQuestion,
      askModelQuestion,
      askPrivateLiteratureQuestion,
      enablePrivateDocuments,
      formatMessage,
      fullStoryEvent,
      mode,
      predictor,
      setQuestionsAndAnswers,
    ],
  );
}
